// src/ui/useContent.ts — Boot-only content fetch hook (P5 D-20, D-24, Pitfall 9).
//
// Runs exactly once per App mount. The effect resolves in order:
//   1. PREVIEW branch (D-16, Pitfall 9):
//        if ?preview=draft in URL AND sessionStorage['hunter-content-draft']
//        parses via ContentBundleSchema → hydrateContent(draft); state.version = 'preview'.
//        If the draft JSON or Zod parse fails, FALL THROUGH to the network branch
//        (do not wedge the boot on a malformed preview draft).
//   2. NETWORK branch (D-20, D-21):
//        fetch('/api/content', { credentials: 'include' }); on 200 with a
//        {bundle} envelope, ContentBundleSchema.parse(bundle) → hydrateContent;
//        state.version = response.version ?? 'default'.
//   3. FALLBACK branch (D-21, D-22):
//        hydrateContent(DEFAULT_BUNDLE); state.version = 'default'.
//        Covers empty {} response, network error, malformed JSON, Zod-invalid
//        bundle — every failure mode lands here with no white screen.
//
// Boot contract: state.ready MUST be false until hydrateContent has run.
// App.tsx gates the game subtree on state.ready === true so D-24 is enforced
// (first React render that could call getProfile happens AFTER hydrate).
//
// Single-fire via `probed` ref — same pattern as useGateAuth.ts.
// DEV-only console.warn (import.meta.env.DEV) — Rollup tree-shakes in prod.
//
// Source: .planning/phases/05-content-pipeline-characters-admin-cms/05-CONTEXT.md D-20..D-25;
//         .planning/phases/05-content-pipeline-characters-admin-cms/05-RESEARCH.md Pattern 4.

import { useEffect, useRef, useState } from 'react';

import { DEFAULT_BUNDLE } from '../shared/content/default-bundle';
import { ContentBundleSchema } from '../shared/content/schema';
import { hydrateContent } from '../sim/content/cache';

import { readPreviewDraft } from './preview-mode';

const CONTENT_URL = '/api/content';
const PREVIEW_QUERY = 'preview';
const PREVIEW_VALUE = 'draft';

export interface ContentState {
  /** True once hydrateContent has run. Game subtree must not mount until true. */
  ready: boolean;
  /** 'pending' before the effect resolves; 'preview' / 'default' / 'v{N}' after. */
  version: string;
  /** Non-null only for unrecoverable failures (v1 never sets — fallback always succeeds). */
  error: string | null;
}

export function useContent(): ContentState {
  const [state, setState] = useState<ContentState>({
    ready: false,
    version: 'pending',
    error: null,
  });
  const probed = useRef(false);

  useEffect(() => {
    if (probed.current) return;
    probed.current = true;

    void (async (): Promise<void> => {
      // ============================================================
      // Preview branch (D-16, Pitfall 9).
      // ============================================================
      const previewQuery =
        new URLSearchParams(window.location.search).get(PREVIEW_QUERY) ===
        PREVIEW_VALUE;
      const draftRaw = readPreviewDraft();
      if (previewQuery && draftRaw !== null) {
        try {
          const draftParsed = ContentBundleSchema.parse(JSON.parse(draftRaw));
          hydrateContent(draftParsed);
          setState({ ready: true, version: 'preview', error: null });
          return;
        } catch (e) {
          if (import.meta.env.DEV) {
            // Draft was malformed — fall through to /api/content so the
            // preview tab still boots to the published content instead of
            // a white screen. The PreviewBanner gates on isPreviewMode()
            // (which also requires the sessionStorage entry), so a
            // malformed draft intentionally silences the banner too.
            console.warn('Preview draft parse failed — falling through to /api/content', e);
          }
        }
      }

      // ============================================================
      // Network branch (D-20, D-21).
      // ============================================================
      try {
        const res = await fetch(CONTENT_URL, { credentials: 'include' });
        if (res.ok) {
          const body = (await res.json()) as unknown;
          if (
            body !== null &&
            typeof body === 'object' &&
            'bundle' in body
          ) {
            const parsed = ContentBundleSchema.parse(
              (body as { bundle: unknown }).bundle,
            );
            hydrateContent(parsed);
            const versionCandidate = (body as { version?: unknown }).version;
            const ver =
              typeof versionCandidate === 'string' ? versionCandidate : 'default';
            setState({ ready: true, version: ver, error: null });
            return;
          }
          // 200 with empty {} envelope — fall through to DEFAULT_BUNDLE.
          // (Documented server contract: unseeded / pointer-miss KV returns {}.)
        }
      } catch (e) {
        if (import.meta.env.DEV) {
          console.warn('Content fetch failed — using default bundle', e);
        }
      }

      // ============================================================
      // Fallback branch (D-21, D-22).
      // ============================================================
      hydrateContent(DEFAULT_BUNDLE);
      setState({ ready: true, version: 'default', error: null });
    })();
  }, []);

  return state;
}
